import { useTranslation } from "react-i18next";
import { MAIN_SITE_URL } from "@/data/categories";

const heroImage =
  "https://readdy.ai/api/search-image?query=A%20bright%20and%20airy%20top-down%20flat%20lay%20of%20diverse%20fashion%20items%20including%20sneakers%2C%20a%20folded%20sweater%2C%20a%20cap%2C%20sunglasses%20and%20a%20small%20backpack%20arranged%20with%20generous%20negative%20space%20on%20a%20soft%20mint%20green%20background%2C%20gentle%20natural%20light%2C%20fresh%20minimalist%20ecommerce%20hero%20composition%2C%20crisp%20and%20clean%20aesthetic&width=1600&height=900&seq=hero-usfans-01&orientation=landscape&nocache=true";

const previewRows = [
  { product: "Item #001", category: "Shoes", seller: "Seller A", price: "—", qc: "Passed", status: "Active" },
  { product: "Item #014", category: "Hoodies", seller: "Seller C", price: "—", qc: "Review", status: "Updated" },
  { product: "Item #032", category: "Bags", seller: "Seller F", price: "—", qc: "Passed", status: "Active" },
  { product: "Item #047", category: "Perfumes", seller: "Seller K", price: "—", qc: "Pending", status: "New" },
];

export default function Hero() {
  const { t } = useTranslation();

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0">
        <img src={heroImage} alt="USFans Spreadsheet product research hub flat lay" className="h-full w-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-b from-background-50/70 via-background-50/40 to-background-50/70" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-accent-100 px-3 py-1.5 text-xs font-semibold text-accent-900">
              <i className="ri-leaf-line text-sm" />
              {t("hero.badge")}
            </span>
            <h1 className="mt-5 text-3xl font-bold leading-tight text-foreground-950 md:text-5xl">{t("hero.title")}</h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-foreground-700 md:text-lg">{t("hero.subtitle")}</p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                {t("hero.ctaPrimary")}
                <i className="ri-arrow-right-up-line text-base" />
              </a>
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-foreground-200 bg-background-50 px-6 py-3 text-sm font-semibold text-foreground-900 transition-colors hover:bg-background-100"
              >
                {t("hero.ctaSecondary")}
              </a>
            </div>

            <p className="mt-4 text-xs text-foreground-500">{t("hero.hint")}</p>
          </div>

          <div className="rounded-2xl border border-background-200 bg-background-50/95 p-4 md:p-5">
            <div className="flex items-center justify-between border-b border-background-200 pb-3">
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <i className="ri-table-line" />
                </span>
                <span className="font-heading text-sm font-semibold text-foreground-950">{t("spreadsheet.title")}</span>
              </div>
              <div className="flex gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-accent-300" />
                <span className="h-2.5 w-2.5 rounded-full bg-primary-300" />
                <span className="h-2.5 w-2.5 rounded-full bg-secondary-300" />
              </div>
            </div>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="text-foreground-500">
                    <th className="whitespace-nowrap pb-2 pr-3 font-medium">Product</th>
                    <th className="whitespace-nowrap pb-2 pr-3 font-medium">Category</th>
                    <th className="whitespace-nowrap pb-2 pr-3 font-medium">Seller</th>
                    <th className="whitespace-nowrap pb-2 pr-3 font-medium">Price</th>
                    <th className="whitespace-nowrap pb-2 pr-3 font-medium">QC</th>
                    <th className="whitespace-nowrap pb-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row) => (
                    <tr key={row.product} className="border-t border-background-200 text-foreground-700">
                      <td className="whitespace-nowrap py-2.5 pr-3 font-medium text-foreground-900">{row.product}</td>
                      <td className="whitespace-nowrap py-2.5 pr-3">{row.category}</td>
                      <td className="whitespace-nowrap py-2.5 pr-3">{row.seller}</td>
                      <td className="whitespace-nowrap py-2.5 pr-3">{row.price}</td>
                      <td className="whitespace-nowrap py-2.5 pr-3">
                        <span
                          className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                            row.qc === "Passed"
                              ? "bg-primary-100 text-primary-800"
                              : row.qc === "Review"
                                ? "bg-accent-100 text-accent-800"
                                : "bg-secondary-100 text-secondary-700"
                          }`}
                        >
                          {row.qc}
                        </span>
                      </td>
                      <td className="whitespace-nowrap py-2.5">{row.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}